import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics/types/events';
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import {
	getFinalMessageId,
	getMauiIdFromUrl,
} from '@atlassian/malleable-ui-components/editor-remix/remix-ids';
import type {
	AddBackendRemixHistoryStackEntryPayload,
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
	UpdateRemixImageHistoryFieldIdPayload,
	RemixHistoryEntry,
} from '@atlassian/malleable-ui-components/types';
import { isAddBackendMauiRemixHistoryStackEntryPayload } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';

type RemixHistoryStackEntry = RemixHistoryEntry['stack'][number];

const isUndoTransaction = (transaction: unknown): boolean =>
	Object.values((transaction as { meta?: Record<string, unknown> }).meta ?? {}).some(
		(value) =>
			typeof value === 'object' &&
			value !== null &&
			'redo' in value &&
			(value as { redo?: unknown }).redo === false &&
			'historyState' in value,
	);

const nodeMatchesStackEntry = (node: PMNode, { fieldId, url }: RemixHistoryStackEntry): boolean => {
	if (fieldId && node.attrs?.id === fieldId) {
		return true;
	}
	if (!url) {
		return false;
	}

	const nodeUrl = node.attrs?.parameters?.macroParams?.url?.value;
	if (typeof nodeUrl !== 'string') {
		return false;
	}

	const entryMauiId = getMauiIdFromUrl(url);
	return (
		nodeUrl === url || (entryMauiId !== undefined && getMauiIdFromUrl(nodeUrl) === entryMauiId)
	);
};

const getActiveStackIndex = (doc: PMNode, stack: RemixHistoryStackEntry[]): number => {
	for (let index = stack.length - 1; index >= 0; index--) {
		let found = nodeMatchesStackEntry(doc, stack[index]);
		if (!found) {
			doc.descendants((node) => {
				if (nodeMatchesStackEntry(node, stack[index])) {
					found = true;
					return false;
				}
				return true;
			});
		}
		if (found) {
			return index;
		}
	}
	return -1;
};

const getUndoneRemixEntry = ({
	historyMap,
	newDoc,
	oldDoc,
}: {
	historyMap: Record<string, RemixHistoryEntry>;
	newDoc: PMNode;
	oldDoc: PMNode;
}): RemixHistoryStackEntry | undefined => {
	for (const historyEntry of Object.values(historyMap)) {
		const oldIndex = getActiveStackIndex(oldDoc, historyEntry.stack);
		if (oldIndex !== -1 && oldIndex !== getActiveStackIndex(newDoc, historyEntry.stack)) {
			return historyEntry.stack[oldIndex];
		}
	}
};

function getMatchingStackIndex(
	stack: RemixHistoryStackEntry[],
	{ fieldId, url }: { fieldId?: string; url?: string },
): number {
	if (!fieldId && !url) {
		return -1;
	}

	for (let index = stack.length - 1; index >= 0; index--) {
		const stackEntry = stack[index];
		if ((url && stackEntry.url === url) || (fieldId && stackEntry.fieldId === fieldId)) {
			return index;
		}
	}
	return -1;
}

/**
 * Find the history entry whose stack contains a step matching the given id
 * (image fieldId or MAUI app id extracted from the stack entry's URL).
 * Returns the conversation key, the entry, and the matched stack index so the
 * caller can trim redo entries above it.
 */
function findHistoryEntryByStackId(
	historyMap: Record<string, RemixHistoryEntry>,
	id: string,
): { entry: RemixHistoryEntry; key: string; stackIndex: number } | undefined {
	for (const [key, entry] of Object.entries(historyMap).reverse()) {
		for (let stackIndex = entry.stack.length - 1; stackIndex >= 0; stackIndex--) {
			const stackEntry = entry.stack[stackIndex];
			if (stackEntry.fieldId === id || getMauiIdFromUrl(stackEntry.url) === id) {
				return { key, entry, stackIndex };
			}
		}
	}
	return undefined;
}

export type RemixHistoryPluginState = {
	/**
	 * The history map is keyed by conversationId and stores every remix result for that conversation.
	 * The active stack entry is derived from the editor node URL/fieldId so editor undo/redo can move
	 * through older entries without mutating this plugin state.
	 */
	historyMap: Record<string, RemixHistoryEntry>;
};

export type RemixHistoryPluginMeta =
	| {
			payload: AddRemixHistoryEntryPayload;
			type: 'addHistoryEntry';
	  }
	| {
			payload: UpdateRemixImageHistoryFieldIdPayload;
			type: 'updateRemixImageHistoryFieldId';
	  }
	| {
			payload: AddRemixHistoryStackEntryPayload;
			type: 'addHistoryStackEntry';
	  }
	| {
			type: 'goBackInHistory';
	  }
	| {
			type: 'goForwardInHistory';
	  }
	| {
			payload: AddBackendRemixHistoryStackEntryPayload;
			type: 'addBackendHistoryStackEntry';
	  };

function reducer(
	state: RemixHistoryPluginState,
	meta?: RemixHistoryPluginMeta,
): RemixHistoryPluginState {
	switch (meta?.type) {
		// for image, the fieldId will be updated after the image is uploaded via updateRemixImageHistoryFieldId
		case 'addHistoryEntry': {
			const {
				admissionBaselineInvocationId,
				conversationId,
				dynamicUiSource,
				dynamicUiSubtype,
				dynamicUiType,
				finalMessage,
				url,
				fieldId,
			} = meta.payload;
			const finalMessageId = getFinalMessageId(finalMessage);
			const historyEntry: RemixHistoryEntry = {
				conversationId,
				dynamicUiSource,
				latestFinalMessage: finalMessage,
				latestFinalMessageId: finalMessageId,
				stack: [
					{
						...(admissionBaselineInvocationId !== undefined && {
							admissionBaselineInvocationId,
						}),
						dynamicUiType,
						dynamicUiSubtype,
						finalMessage,
						finalMessageId,
						url,
						fieldId,
					},
				],
			};

			return {
				historyMap: {
					...state.historyMap,
					[conversationId]: historyEntry,
				},
			};
		}

		case 'updateRemixImageHistoryFieldId': {
			const { conversationId, fieldId } = meta.payload;
			const historyEntry = state.historyMap[conversationId];
			if (!historyEntry) {
				return state;
			}

			const currentStackIndex = historyEntry.stack.length - 1;
			if (currentStackIndex < 0) {
				return state;
			}

			const updatedHistoryEntry = {
				...historyEntry,
				stack: historyEntry.stack.map((stackEntry, index) =>
					index === currentStackIndex ? { ...stackEntry, fieldId } : stackEntry,
				),
			};
			return {
				historyMap: {
					...state.historyMap,
					[conversationId]: updatedHistoryEntry,
				},
			};
		}

		case 'addHistoryStackEntry': {
			const {
				admissionBaselineInvocationId,
				conversationId,
				dynamicUiType,
				dynamicUiSubtype,
				finalMessage,
				url,
				fieldId,
				previousUrl,
				previousFieldId,
			} = meta.payload;
			const finalMessageId = getFinalMessageId(finalMessage);
			const historyEntry = state.historyMap[conversationId];
			if (!historyEntry) {
				return state;
			}

			const currentStackIndex = getMatchingStackIndex(historyEntry.stack, {
				url: previousUrl,
				fieldId: previousFieldId,
			});
			const stackEntriesToKeep =
				currentStackIndex === -1
					? historyEntry.stack
					: historyEntry.stack.slice(0, currentStackIndex + 1);

			const updatedHistoryEntry: RemixHistoryEntry = {
				...historyEntry,
				latestFinalMessage: finalMessage,
				latestFinalMessageId: finalMessageId,
				stack: [
					...stackEntriesToKeep,
					{
						...(admissionBaselineInvocationId !== undefined && {
							admissionBaselineInvocationId,
						}),
						dynamicUiType,
						dynamicUiSubtype,
						finalMessage,
						finalMessageId,
						url,
						fieldId,
					},
				],
			};

			return {
				historyMap: {
					...state.historyMap,
					[conversationId]: updatedHistoryEntry,
				},
			};
		}

		// Append a stack entry for a backend-initiated update (bridge stream).
		// No conversationId / finalMessage in payload — inherit both from the
		// existing entry, located by stack-id search. The payload is a
		// discriminated union: MAUI variant carries previousMauiId + new URL;
		// image variant carries previousFieldId + new fieldId.
		case 'addBackendHistoryStackEntry': {
			const { payload } = meta;
			const isMauiPayload = isAddBackendMauiRemixHistoryStackEntryPayload(payload);
			const lookupId = isMauiPayload ? payload.previousMauiId : payload.previousFieldId;
			const found = findHistoryEntryByStackId(state.historyMap, lookupId);
			if (!found) {
				return state;
			}

			const { key, entry, stackIndex } = found;
			const matchedStackEntry = entry.stack[stackIndex];
			const inheritedFinalMessage = entry.latestFinalMessage ?? matchedStackEntry.finalMessage;
			const inheritedFinalMessageId =
				entry.latestFinalMessageId ??
				getFinalMessageId(entry.latestFinalMessage) ??
				matchedStackEntry.finalMessageId ??
				getFinalMessageId(matchedStackEntry.finalMessage);

			// Prefer the payload's `dynamicUiSubtype` when the processor passes one —
			// a backend edit that changed the type (e.g. line-chart → bar-chart, or a
			// new tree map) — so the style picker reflects the new type. Otherwise
			// inherit the matched entry's subtype (e.g. the backend omitted it).
			const newStackEntry = isMauiPayload
				? {
						dynamicUiType: payload.dynamicUiType ?? matchedStackEntry.dynamicUiType,
						dynamicUiSubtype: payload.dynamicUiSubtype ?? matchedStackEntry.dynamicUiSubtype,
						finalMessage: inheritedFinalMessage,
						finalMessageId: inheritedFinalMessageId,
						url: payload.url,
						fieldId: undefined,
					}
				: {
						dynamicUiType: matchedStackEntry.dynamicUiType,
						dynamicUiSubtype: matchedStackEntry.dynamicUiSubtype,
						finalMessage: inheritedFinalMessage,
						finalMessageId: inheritedFinalMessageId,
						url: undefined,
						fieldId: payload.fieldId,
					};

			const updatedHistoryEntry: RemixHistoryEntry = {
				...entry,
				stack: [...entry.stack.slice(0, stackIndex + 1), newStackEntry],
			};

			return {
				historyMap: {
					...state.historyMap,
					[key]: updatedHistoryEntry,
				},
			};
		}

		case 'goBackInHistory':
		case 'goForwardInHistory':
			return state;

		default:
			return state;
	}
}

export const remixHistoryPluginKey: PluginKey<RemixHistoryPluginState> =
	new PluginKey<RemixHistoryPluginState>('remixHistoryPlugin');

export function createRemixHistoryPmPlugin({
	api,
	dispatch,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	dispatch: Dispatch;
}): SafePlugin {
	return new SafePlugin({
		key: remixHistoryPluginKey,

		state: {
			init(): RemixHistoryPluginState {
				return {
					historyMap: {},
				};
			},

			apply(tr, pluginState, oldState, newState): RemixHistoryPluginState {
				let newPluginState = pluginState;

				const meta = tr.getMeta(remixHistoryPluginKey);
				if (meta) {
					newPluginState = reducer(newPluginState, meta);
				}

				if (isUndoTransaction(tr) && oldState?.doc && newState?.doc) {
					const undoneEntry = getUndoneRemixEntry({
						historyMap: pluginState.historyMap,
						newDoc: newState.doc,
						oldDoc: oldState.doc,
					});
					if (undoneEntry) {
						api?.analytics?.actions.fireAnalyticsEvent({
							eventType: 'ui',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'remixUndo',
							attributes: {
								remixType: undoneEntry.dynamicUiType,
								remixSubtype: undoneEntry.dynamicUiSubtype,
							},
						} as AnalyticsEventPayload);
					}
				}

				if (newPluginState !== pluginState) {
					dispatch(remixHistoryPluginKey, newPluginState);
				}

				return newPluginState;
			},
		},
	});
}
